import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';

import { ReactComponent as TransactionIcon } from '../../../assets/icons/sidebar/sidebar-payment.svg';
import { ReactComponent as SlashIcon } from '../../../assets/icons/slash.svg';
import { ReactComponent as StopIcon } from '../../../assets/icons/stop-circle.svg';
import DefaultApiLimit from '../../../config/defaultLimit';
// import { PortalType } from '../../../config/enum';
import { usePortal } from '../../../contexts/PortalContext/PortalContext';
import { CommercialSessionInterface } from '../../../interface/charging-session.interface';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import {
  disableSessionFiltersApplied,
  fetchAllSessions,
  fetchListSessionFilters,
  updateSessionFilters,
  setSessionSortData,
  refreshChargingSessions,
} from '../../../redux/slices/ChargingSessionSlice';
import { setSelectedDevice } from '../../../redux/slices/DeviceManagementSlice';
import { setSelectedStation } from '../../../redux/slices/LocationManagementSidePanelSlice';
import { setNotificationMessage } from '../../../redux/slices/NotificationManagementSlice';
import { setPageTitle } from '../../../redux/slices/PageTitleSlice';
import { setSidePanelData } from '../../../redux/slices/TableSidePanelSlice';
import CustomActionBar from '../../../shared-components/custom-action-bar/CustomActionBar';
import CustomDateRangePicker from '../../../shared-components/custom-date-range/CustomDateRangePicker';
import CustomRadioButton from '../../../shared-components/custom-radio-button/CustomRadioButton';
import CustomTable from '../../../shared-components/custom-table/CustomTable';
import ModalBox from '../../../shared-components/modal-box/ModalBox';
import SuccessNotification from '../../../shared-components/success-notification/SuccessNotification';
import CommercialSessionSidePanel from '../../../shared-components/table-side-panel/CommercialSessionSidePanel';
import DeviceSidePanel from '../../../shared-components/table-side-panel/DeviceSidePanel';
import FacilityDetailsSidePanel from '../../../shared-components/table-side-panel/FacilityDetailsSidePanel';
import { useTableSidePanel } from '../../../shared-components/table-side-panel/hooks/useTableSidePanel';
import LocationSidePanel from '../../../shared-components/table-side-panel/LocationSidePanel';
import TariffSidePanel from '../../../shared-components/table-side-panel/TariffSidePanel';
import { GetParametersFromCommonFilters } from '../../../shared-functions/CommonFilters';
import ConvertUTCtoDateTime from '../../../shared-functions/ConvertUTCtoDateTime';
import {
  changeDateFormat,
  getDaysBetween,
  removeEmptyKeys,
} from '../../../shared-functions/DateTime';
import { exportCSV, exportPDF } from '../../../shared-functions/ExportFile';
import FormatDuration from '../../../shared-functions/FormatDuration';
import { permissionCheck } from '../../../shared-functions/JWTDocodeFunction';
import occpEvent from '../../../utils/api/ocpp-events';
import paymentApi from '../../../utils/api/payments';

interface LiveDurationProperties {
  startTime: string | Date;
}

/**
 * Isolated live duration ticker component for in-progress charging sessions.
 * @param {LiveDurationProperties} properties - Component properties containing session start time.
 * @returns {React.JSX.Element} Formatted live duration string.
 */
function LiveDuration({ startTime }: Readonly<LiveDurationProperties>): React.JSX.Element {
  const [currentTime, setCurrentTime] = useState(Date.now());

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(Date.now());
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  const startTimestamp = new Date(startTime).getTime();
  if (Number.isNaN(startTimestamp)) {
    return <span>-</span>;
  }

  const totalSeconds = Math.max(0, Math.floor((currentTime - startTimestamp) / 1000));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  return (
    <span>
      {`${String(hours).padStart(2, '0')}h ${String(minutes).padStart(2, '0')}m ${String(seconds).padStart(2, '0')}s`}
    </span>
  );
}

/**
 * Commercial Session Table Component
 * @param {any} param0 - The param0
 * @param {any} param0.currentPage - The currentPage prop
 * @param {any} param0.setCurrentPage - The setCurrentPage prop
 * @param {any} param0.chargingStationIds - The chargingStationIds prop
 * @param {any} param0.chargingPoints - The chargingPoints prop
 */
function CommercialSessionTable({
  currentPage,
  setCurrentPage,
  chargingStationIds,
  chargingPoints,
  defaultStatus = '',
  hideStatusBar = false,
}: Readonly<{
  currentPage: string;
  setCurrentPage: (value: string) => void;
  chargingStationIds?: string;
  chargingPoints?: string;
  defaultStatus?: string;
  hideStatusBar?: boolean;
}>) {
  const { t, i18n } = useTranslation(['transaction', 'sidePanel', 'account']);
  const dispatch = useAppDispatch();
  // @ts-ignore
  const {
    payments: sessions,
    limit,
    page,
    totalPages,
    totalResults,
    loading,
    filterListLoading,
    filters,
    isFilterUpdated,
    sortField,
    sortBy,
    TotalFinising,
    totalInprocess,
    totalterminated,
    // @ts-ignore
  } = useAppSelector((state) => state.chargingSession);
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [searchParameters, setSearchParameters] = useSearchParams();
  const { portalType } = usePortal();
  const navigate = useNavigate();
  const location = useLocation();
  const { deviceDetails } = useAppSelector((state) => state.tableSidePanel);
  const [hiddenColumns, setHiddenColumns] = useState<string[]>([]);
  const [activeStatus, setActiveStatus] = useState<string>(defaultStatus || '');
  const isStatusClicked = React.useRef(false);
  const pageReference = React.useRef<number>(page || 1);

  useEffect(() => {
    if (page) {
      pageReference.current = page;
    }
  }, [page]);

  const currentDate = changeDateFormat(new Date().toString());
  const [dateRange, setDateRange] = useState<{ startDate: string; endDate: string }>({
    startDate: currentDate,
    endDate: currentDate,
  });
  const [isAutoRefreshActive, setIsAutoRefreshActive] = useState<boolean>(true);

  const [exportLoader, setExportLoader] = useState<boolean>(false);

  /**
   * Clears the date range values.
   */
  const clearDateRange = () => {
    setDateRange({
      startDate: '',
      endDate: '',
    });
    setIsAutoRefreshActive(false);
  };

  /**
   * Handle date range change.
   * @param {string} start - The start date string.
   * @param {string} end - The end date string.
   */
  const handleDateRangeChange = (start: string, end: string) => {
    const today = changeDateFormat(new Date().toString());
    setDateRange({ startDate: start, endDate: end });
    if (start !== today || end !== today) {
      setIsAutoRefreshActive(false);
    }
  };

  /**
   * Render the date filters.
   * @returns {React.JSX.Element} The rendered date filter elements.
   */
  const renderDateFilters = () => {
    return (
      <div className="date-content">
        <CustomDateRangePicker
          startDate={dateRange.startDate}
          endDate={dateRange.endDate}
          onChange={handleDateRangeChange}
          onClear={clearDateRange}
          placeholder="DD-MMM-YYYY - DD-MMM-YYYY"
          disableDates="future"
        />
      </div>
    );
  };

  /**
   * Fetch the session details in redux
   * @param {any} data - The data
   * @param {boolean} [force] - Whether to force fetch even if loading
   */
  const fetchSessions = (data: any, force = false) => {
    if (getDaysBetween(data.startDate, data.endDate) > 31) {
      dispatch(
        setNotificationMessage({
          message: t('dateRangeLimitMessage'),
          status: true,
          type: 'error',
          code: 400,
        }),
      );
      return;
    }

    if (!loading || force) {
      const extraParameters = {
        ...(chargingStationIds ? { 'chargingStationIds[]': chargingStationIds } : {}),
        ...(chargingPoints ? { 'chargingPoints[]': chargingPoints } : {}),
        ...(activeStatus ? { status: activeStatus } : {}),
      };
      dispatch(fetchAllSessions({ ...data, ...extraParameters }));
    }
  };

  /**
   * Function to refresh data
   * @param {boolean} [force] - Whether to force refresh
   */
  const handleRefresh = (force = false) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    const sortValue = sortField && sortBy ? { sortField, sortBy } : {}; // no sort if empty
    fetchSessions(
      {
        ...queryParameters,
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
        page,
        limit,
        ...sortValue,
      },
      force,
    );
  };

  /**
   * Function to open device details view
   * @param {any} _ - The _
   * @param {any} newTab - The newTab
   */
  const handleOpenModelView = (_: any, newTab: boolean) => {
    if (deviceDetails) {
      dispatch(
        setSelectedDevice({
          id: deviceDetails?.id,
          name: deviceDetails?.chargingPointName,
          chargingPointSn: deviceDetails?.chargingPointSn,
          modelSeries: deviceDetails?.modelSeries,
        }),
      );

      const sourcePath =
        location.state?.source || window.location.pathname + window.location.search;
      if (newTab) {
        window.open(
          `/ev-admin/charger-management/${deviceDetails.id}/device-details?searchBy=${deviceDetails.chargingPointName}`,
          '_blank',
        );
      } else {
        navigate(
          `/ev-admin/charger-management/${deviceDetails.id}/device-details?searchBy=${deviceDetails.chargingPointName}`,
          { state: { source: sourcePath } },
        );
      }
    }
  };

  /**
   * Function to open station details view
   * @param {any} chargingStationDate - The chargingStationDate
   * @param {any} newTab - The newTab
   */
  const handleOpenModelStationView = (chargingStationDate: any, newTab: boolean) => {
    if (chargingStationDate) {
      dispatch(
        setSelectedStation({
          id: chargingStationDate?.chargingStationId,
          name: chargingStationDate?.chargingStationName || chargingStationDate?.name,
        }),
      );
      if (newTab) {
        const url = `/ev-admin/station-expanded/${chargingStationDate.chargingStationId}/station-dashboard?searchBy=${chargingStationDate.chargingStationName || chargingStationDate.name}&source=${location.pathname}${location.search}`;
        window.open(url, '_blank');
      } else {
        navigate(
          {
            pathname: `/ev-admin/station-management/${chargingStationDate.chargingStationId}/station-dashboard`,
            search: `?searchBy=${chargingStationDate.chargingStationName || chargingStationDate.name}`,
          },
          { state: { source: location.pathname + location.search } },
        );
      }
    } else {
      // eslint-disable-next-line no-console
      console.log('Error while opening device in separate page');
    }
  };

  const [selectedSessionRow, setSelectedSessionRow] = useState<CommercialSessionInterface | null>(
    null,
  );

  /**
   * Function to handle side panel open on table row click
   * @param {string} sessionId - The session ID from clicked row
   * @param {CommercialSessionInterface} row - The clicked session row data
   */
  const handleOpenSidePanelOnRowClick = (sessionId: string, row: CommercialSessionInterface) => {
    setSelectedSessionRow(row);
    const parameters = new URLSearchParams(searchParameters);
    const sidePanelKeys = [
      'accountCustomId',
      'deviceId',
      'connectorDeviceId',
      'stationId',
      'facilityId',
      'userCustomId',
      'userId',
      'utilityId',
      'transactionObjectId',
      'roleId',
      'sessionId',
      'cdrObjectId',
      'paymentDetailsId',
      'personalChargerId',
      'tariffId',
      'action',
    ];
    sidePanelKeys.forEach((key) => parameters.delete(key));
    parameters.set(
      'sessionId',
      sessionId || row?.id || (row as any)?._id || (row as any)?.transactionId || '101',
    );
    setSearchParameters(parameters);
    dispatch(
      setSidePanelData({
        key: 'CommercialSessions',
        value: row?.id || (row as any)?._id || sessionId,
      }),
    );
  };

  const {
    isEditMode,
    openStatus,
    setOpenStatus,
    modalView,
    setModalView,
    handleEditModeChange,
    handleSidePanelClose,
    renderSidePanelTopLeftContent,
  } = useTableSidePanel({
    onRefresh: handleRefresh,
    onOpenStationDetails: handleOpenModelStationView,
    onOpenDeviceDetails: handleOpenModelView,
    sessions,
  });

  /**
   * Function to handle radio button change
   * @param {any} name - The name
   * @param {any} value - The value
   */
  const handleAvailabilityChange = (name: string, value: string) => {
    setCurrentPage(value);
  };

  /**
   * Modal box close function
   * @param {any} message - The message
   */
  const showSuccessNotification = (message: string) => {
    if (message) {
      setOpenStatus(true);
      setModalView({
        title: '',
        header: false,
        content: (
          <SuccessNotification
            title={message}
            description=""
            notificationCloseTimer={3000}
            onNotificationClose={() => {
              setOpenStatus(false);
              handleRefresh(true);
            }}
          />
        ),
        rightCorner: '',
        notification: true,
      });
    } else {
      setOpenStatus(false);
    }
  };

  /**
   * Handle auto refresh toggle change
   * @param {boolean} nextState - Next auto refresh state
   */
  const handleAutoRefreshToggle = (nextState: boolean) => {
    setIsAutoRefreshActive(nextState);
    if (nextState) {
      const today = changeDateFormat(new Date().toString());
      if (dateRange.startDate !== today || dateRange.endDate !== today) {
        setDateRange({
          startDate: today,
          endDate: today,
        });
      }
    }
  };

  /**
   * Function to auto refresh
   */
  const handleAutoRefresh = () => {
    const today = changeDateFormat(new Date().toString());
    if (dateRange.startDate !== today || dateRange.endDate !== today) {
      return;
    }
    const queryParameters = GetParametersFromCommonFilters(filters);
    const activePage = pageReference.current || page || 1;

    dispatch(
      refreshChargingSessions({
        ...queryParameters,
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
        page: activePage,
        limit,
        sortBy,
        sortField,
        ...(chargingStationIds ? { 'chargingStationIds[]': chargingStationIds } : {}),
        ...(chargingPoints ? { 'chargingPoints[]': chargingPoints } : {}),
        ...(activeStatus ? { status: activeStatus } : {}),
      }),
    );
  };

  /**
   * Current columns for export
   */
  const currentColumns = [
    { label: t('sNo'), accessorKey: '__serial__' },
    {
      label: t('OCPP Transaction Id'),
      accessorKey: 'transactionId',
    },
    {
      label: t('sessionStartTime'),
      accessorKey: 'transactionStartsAt',
    },
    {
      label: t('SessionEndTime'),
      accessorKey: 'transactionEndsAt',
    },
    {
      label: t('PartnerName'),
      accessorKey: 'facilityAccountName',
    },
    {
      label: t('stationName'),
      accessorKey: 'chargingStation',
    },
    {
      label: t('chargerName'),
      accessorKey: 'chargingPointName',
    },
    {
      label: t('chargerSN'),
      accessorKey: 'chargingPointSn',
    },
    {
      label: t('tariffId'),
      accessorKey: 'tariff',
    },
    {
      label: t('ocpiSessionId'),
      accessorKey: 'ocpiId',
    },
    {
      label: t('powerConsumed'),
      accessorKey: 'powerConsumedInKwh',
    },
    {
      label: t('amount'),
      accessorKey: 'totalAmount',
    },
    {
      label: t('duration'),
      accessorKey: 'durationSeconds',
    },
    {
      label: t('status'),
      accessorKey: 'status',
    },
    {
      label: t('created'),
      accessorKey: 'createdAt',
    },
    {
      label: t('lastUpdated'),
      accessorKey: 'updatedAt',
    },
  ];

  /**
   * Filtering columns for export without hidden columns
   */
  const exportCurrentColumns = currentColumns.filter(
    (col) => !hiddenColumns.includes(col.accessorKey),
  );

  /**
   * Function to export data in PDF or CSV format
   * @param {string} type - The export format type ('csv' or 'pdf')
   */
  const exportDataPdfOrCSV = (type: string) => {
    if (
      dateRange.startDate &&
      dateRange.endDate &&
      getDaysBetween(dateRange.startDate, dateRange.endDate) > 31
    ) {
      dispatch(
        setNotificationMessage({
          message: t('dateRangeLimitMessage'),
          status: true,
          type: 'error',
          code: 400,
        }),
      );
      return;
    }

    setExportLoader(true);
    const queryParameters = GetParametersFromCommonFilters(filters);
    const payload: Record<string, any> = {
      ...queryParameters,
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
      ...(chargingStationIds ? { 'chargingStationIds[]': chargingStationIds } : {}),
      ...(chargingPoints ? { 'chargingPoints[]': chargingPoints } : {}),
      ...(activeStatus ? { status: activeStatus } : {}),
    };
    if (sortBy) payload.sortBy = sortBy;
    if (sortField) payload.sortField = sortField;

    paymentApi
      .fetchChargingSessionsExport(removeEmptyKeys(payload))
      .then((response: any) => {
        const exportDataList = Array.isArray(response?.data)
          ? response.data
          : response?.data?.results || response?.results || [];

        const headers = exportCurrentColumns.map((col) => col.label);

        const rows = exportDataList.map((row: CommercialSessionInterface, index: number) =>
          exportCurrentColumns.map((col) => {
            switch (col.accessorKey) {
              case '__serial__':
                return String(index + 1);

              case 'createdAt':
                return row?.createdAt ? ConvertUTCtoDateTime(row.createdAt as any) : 'N/A';

              case 'updatedAt':
                return (row as any)?.updatedAt
                  ? ConvertUTCtoDateTime((row as any).updatedAt as any)
                  : 'N/A';

              case 'transactionStartsAt':
                return row.transactionStartsAt
                  ? ConvertUTCtoDateTime(row.transactionStartsAt as any)
                  : 'N/A';

              case 'transactionEndsAt':
                return row.transactionEndsAt
                  ? ConvertUTCtoDateTime(row.transactionEndsAt as any)
                  : 'N/A';

              case 'status':
                return row.status ? t(row.status) : 'N/A';

              case 'durationSeconds':
                return FormatDuration(row.durationSeconds, { formatBy: 'sec' });

              case 'tariff':
                return (
                  row?.tariff?.tariffId ||
                  (row?.tariff as any)?._id ||
                  (row as any)?.tariffId ||
                  'N/A'
                );

              case 'facilityAccountName':
                return (row as any)?.facilityAccountName || row?.accountCustomId || 'N/A';

              case 'chargingStation':
                return (
                  row.chargingStationName ||
                  (typeof (row as any).chargingStation === 'object'
                    ? (row as any).chargingStation?.name
                    : (row as any).chargingStation) ||
                  'N/A'
                );

              case 'chargingPointName':
                return row.chargingPointName || (row as any)?.deviceName || 'N/A';

              case 'powerConsumedInKwh': {
                const energy =
                  row?.powerConsumedInKwh ?? row?.energyKwh ?? (row as any)?.energyConsumedKwh;
                return energy !== undefined && energy !== null && energy !== ''
                  ? Number(energy).toFixed(3)
                  : 'N/A';
              }

              case 'totalAmount': {
                const amount = row?.totalAmount ?? row?.totalCost ?? (row as any)?.amount;
                return amount !== undefined && amount !== null && amount !== ''
                  ? Number(amount).toFixed(2)
                  : 'N/A';
              }

              default: {
                const fieldValue = (row as any)[col.accessorKey];
                if (fieldValue && typeof fieldValue === 'object') {
                  return fieldValue.name || fieldValue.id || fieldValue.tariffId || 'N/A';
                }
                return fieldValue ?? 'N/A';
              }
            }
          }),
        );

        setExportLoader(false);

        if (rows.length > 0) {
          if (type === 'csv') {
            exportCSV(headers, rows, 'commercial-sessions.csv');
          } else {
            exportPDF(headers, rows, 'commercial-sessions.pdf');
          }
        }
      })
      .catch((error: any) => {
        setExportLoader(false);
        dispatch(
          setNotificationMessage({
            message: error?.response?.data?.message || t('errorMessage'),
            status: true,
            type: 'error',
            code: error?.response?.data?.code || 500,
          }),
        );
      });
  };
  /**
   * Function to handle stop button click
   * @param {any} data session data or list of ids
   */
  const handleStop = (data: CommercialSessionInterface | string[]) => {
    let itemsToProcess: CommercialSessionInterface[] = [];

    if (Array.isArray(data)) {
      itemsToProcess = sessions.filter((session: any) => data.includes(session.id));
    } else {
      itemsToProcess = [data];
    }

    if (itemsToProcess.length === 0) return;

    const promises = itemsToProcess.map((item) => {
      const payload = {
        chargingPointSn: item.chargingPointSn,
        transactionId: item.transactionId,
      };
      return occpEvent.remoteStopTransaction(payload);
    });

    Promise.all(promises)
      .then(() => {
        showSuccessNotification(
          itemsToProcess.length > 1
            ? t('transactionsStopRequestSuccess')
            : t('transactionStopRequestSuccess'),
        );
        handleRefresh(true);
        setTimeout(() => {
          handleRefresh(true);
        }, 1500);
        setTimeout(() => {
          handleRefresh(true);
        }, 3500);
        if (Array.isArray(data)) {
          setSelectedRows([]);
        }
      })
      .catch((error) => {
        dispatch(
          setNotificationMessage({
            status: true,
            message: error?.response?.data?.message || t('errorMessage'),
            type: 'error',
            code: 0,
          }),
        );
      });
  };

  /**
   * Function to handle force stop button click
   * @param {any} data session data or list of ids
   */
  const handleForceStop = (data: CommercialSessionInterface | string[]) => {
    let itemsToProcess: CommercialSessionInterface[] = [];

    if (Array.isArray(data)) {
      itemsToProcess = sessions.filter((session: any) => data.includes(session.id));
    } else {
      itemsToProcess = [data];
    }

    if (itemsToProcess.length === 0) return;

    const promises = itemsToProcess.map((item) => {
      const payload = {
        chargingPointSn: item.chargingPointSn,
        transactionId: item.transactionId,
        forceStop: 'true',
      };
      return occpEvent.remoteStopTransaction(payload);
    });

    Promise.all(promises)
      .then(() => {
        showSuccessNotification(
          itemsToProcess.length > 1
            ? t('transactionsForceStopRequestSuccess')
            : t('transactionForceStopRequestSuccess'),
        );
        handleRefresh(true);
        setTimeout(() => {
          handleRefresh(true);
        }, 1500);
        setTimeout(() => {
          handleRefresh(true);
        }, 3500);
        if (Array.isArray(data)) {
          setSelectedRows([]);
        }
      })
      .catch((error) => {
        dispatch(
          setNotificationMessage({
            status: true,
            message: error?.response?.data?.message || t('errorMessage'),
            type: 'error',
            code: 0,
          }),
        );
      });
  };

  const column = [
    {
      label: t('OCPP Transaction Id'),
      accessorKey: 'transactionId' as keyof CommercialSessionInterface,
      /**
       * Custom data for the transaction ID column
       * @param {CommercialSessionInterface} rowData session data
       * @returns {React.ReactElement} JSX element
       */
      customData: (rowData: CommercialSessionInterface) => {
        let statusClass = '';
        if (rowData?.status) {
          const statusLower = rowData.status.toLowerCase();
          if (statusLower === 'finished') {
            statusClass = 'status-finished';
          } else if (
            statusLower === 'inprogress' ||
            statusLower === 'in progress' ||
            statusLower === 'in_progress'
          ) {
            statusClass = 'status-inProgress';
          } else if (statusLower === 'pending') {
            statusClass = 'status-pending';
          } else if (statusLower === 'terminated') {
            statusClass = 'status-terminated';
          } else if (statusLower === 'failed') {
            statusClass = 'status-failed';
          }
        }

        return (
          <div className="transaction-id-col">
            <span>{rowData.transactionId || '-'}</span>
            {(rowData as any).isChargingPointCheck && (
              <span className={`status-badge ${statusClass}`}>
                <span className="status-text capitalize admin-badge-text">Admin</span>
              </span>
            )}
          </div>
        );
      },
    },

    {
      label: t('sessionStartTime'),
      accessorKey: 'transactionStartsAt' as keyof CommercialSessionInterface,
      sortAllow: true,
      /**
       * Custom data for the charging started at column
       * @param {any} rowData session data
       * @returns {any} JSX element
       */
      customData: (rowData: CommercialSessionInterface) => (
        <span>{ConvertUTCtoDateTime(rowData?.transactionStartsAt as any)}</span>
      ),
    },
    {
      label: t('SessionEndTime'),
      accessorKey: 'transactionEndsAt' as keyof CommercialSessionInterface,
      sortAllow: true,
      /**
       * Custom data for the charging ended at column
       * @param {any} rowData session data
       * @returns {any} JSX element
       */
      customData: (rowData: CommercialSessionInterface) => (
        <span>{ConvertUTCtoDateTime(rowData?.transactionEndsAt as any)}</span>
      ),
    },
    {
      label: t('PartnerName'),
      accessorKey: 'facilityAccountName' as keyof CommercialSessionInterface,
      queryParamsKey: 'accountCustomId',
      queryParamsValue: 'accountCustomId',
      sidePanelComponent: (
        <FacilityDetailsSidePanel
          actionActive
          isEditMode={isEditMode}
          onEditModeChange={handleEditModeChange}
          fetchIdFrom="queryParams"
          onSuccess={() => {
            handleEditModeChange(false);
            handleRefresh();
          }}
        />
      ),
    },

    {
      label: t('stationName'),
      accessorKey: 'chargingStation' as keyof CommercialSessionInterface,
      queryParamsKey: 'stationId',
      queryParamsValue: 'chargingStationId',
      sidePanelComponent: (
        <LocationSidePanel
          fetchIdFrom="queryParams"
          initialIsEditMode={isEditMode}
          onEditModeChange={handleEditModeChange}
          onSuccess={() => {
            handleEditModeChange(false);
            handleRefresh();
          }}
        />
      ),
    },
    {
      label: t('chargerName'),
      accessorKey: 'chargingPointName' as keyof CommercialSessionInterface,
      queryParamsKey: 'deviceId',
      queryParamsValue: 'chargingPointSn',
      sidePanelComponent: (
        <DeviceSidePanel
          fetchIdFrom="queryParams"
          isEditMode={isEditMode}
          onEditModeChange={handleEditModeChange}
          onSuccess={() => {
            handleEditModeChange(false);
            handleRefresh();
          }}
        />
      ),
    },
    {
      label: t('chargerSN'),
      accessorKey: 'chargingPointSn' as keyof CommercialSessionInterface,
      queryParamsKey: 'deviceId',
      queryParamsValue: 'chargingPointSn',
      sidePanelComponent: (
        <DeviceSidePanel
          fetchIdFrom="queryParams"
          isEditMode={isEditMode}
          onEditModeChange={handleEditModeChange}
          onSuccess={() => {
            handleEditModeChange(false);
            handleRefresh();
          }}
        />
      ),
    },
    {
      label: t('tariffId'),
      accessorKey: 'tariff' as keyof CommercialSessionInterface,
      sortAllow: false,
      queryParamsKey: 'tariffId',
      sidePanelComponent: <TariffSidePanel fetchIdFrom="queryParams" isPartner={false} />,
      disableNewTab: true,
      /**
       * Custom data for the tariff ID column
       * @param {CommercialSessionInterface} rowData - Session data
       * @returns {any} Tariff ID element
       */
      customData: (rowData: CommercialSessionInterface) => {
        const tariffId = rowData?.tariff?.tariffId || (rowData?.tariff as any)?._id;

        if (!tariffId) {
          return <span>-</span>;
        }

        return (
          <span
            className="table-row-each-data hyperlink"
            role="presentation"
            onClick={(event) => {
              event.stopPropagation();
              const parameters = new URLSearchParams(searchParameters);
              const sidePanelKeys = [
                'accountCustomId',
                'deviceId',
                'connectorDeviceId',
                'stationId',
                'facilityId',
                'userCustomId',
                'userId',
                'utilityId',
                'transactionObjectId',
                'roleId',
                'sessionId',
                'cdrObjectId',
                'paymentDetailsId',
                'personalChargerId',
                'tariffId',
                'action',
              ];
              sidePanelKeys.forEach((key) => parameters.delete(key));
              parameters.set('tariffId', tariffId);
              setSearchParameters(parameters);
              dispatch(
                setSidePanelData({
                  key: 'Sessions',
                  value: rowData.id,
                }),
              );
            }}>
            {tariffId}
          </span>
        );
      },
    },
    {
      label: t('ocpiSessionId'),
      accessorKey: 'ocpiId' as keyof CommercialSessionInterface,
    },
    {
      label: t('powerConsumed'),
      accessorKey: 'powerConsumedInKwh' as keyof CommercialSessionInterface,
      /**
       * Custom data for the power consumed column
       * @param {CommercialSessionInterface} rowData session data
       * @returns {React.ReactElement} JSX element
       */
      customData: (rowData: CommercialSessionInterface) => {
        const energy =
          rowData.powerConsumedInKwh ?? rowData.energyKwh ?? (rowData as any).energyConsumedKwh;
        return (
          <span>
            {energy !== undefined && energy !== null && energy !== ''
              ? Number(energy).toFixed(3)
              : ''}
          </span>
        );
      },
    },
    {
      label: t('amount'),
      accessorKey: 'totalAmount' as keyof CommercialSessionInterface,
      /**
       * Custom data for the power consumed column
       * @param {CommercialSessionInterface} rowData session data
       * @returns {React.ReactElement} JSX element
       */
      customData: (rowData: CommercialSessionInterface) => {
        const amount = rowData.totalAmount ?? rowData.totalCost ?? (rowData as any).amount;
        return (
          <span>
            {amount !== undefined && amount !== null && amount !== ''
              ? `${Number(amount).toFixed(2)} ${rowData?.currency || ''}`
              : ''}
          </span>
        );
      },
    },
    {
      label: t('duration'),
      accessorKey: 'durationSeconds' as keyof CommercialSessionInterface,
      /**
       * Custom data for the duration column
       * @param {CommercialSessionInterface} rowData session data
       * @returns {React.ReactElement} JSX element
       */
      customData: (rowData: CommercialSessionInterface) => {
        const status = rowData?.status?.toLowerCase();

        const isInProgress = ['inprogress', 'in progress', 'in_progress'].includes(status || '');

        if (isInProgress && rowData?.transactionStartsAt) {
          return <LiveDuration startTime={rowData.transactionStartsAt} />;
        }

        return <span>{FormatDuration(rowData.durationSeconds, { formatBy: 'sec' })}</span>;
      },
    },
    // {
    //   label: t('userName'),
    //   accessorKey: 'endConsumer' as keyof CommercialSessionInterface,
    //   sortAllow: false,
    // },

    {
      label: t('status'),
      accessorKey: 'status' as keyof CommercialSessionInterface,
      /**
       * Custom data for the status column
       * @param {any} rowData session data
       * @returns {any} JSX element
       */
      customData: (rowData: CommercialSessionInterface) => {
        let statusClass = '';
        if (rowData?.status) {
          const statusLower = rowData.status.toLowerCase();
          if (statusLower === 'finished') {
            statusClass = 'status-finished';
          } else if (
            statusLower === 'inprogress' ||
            statusLower === 'in progress' ||
            statusLower === 'in_progress'
          ) {
            statusClass = 'status-inProgress';
          } else if (statusLower === 'pending') {
            statusClass = 'status-pending';
          } else if (statusLower === 'terminated') {
            statusClass = 'status-terminated';
          } else if (statusLower === 'failed') {
            statusClass = 'status-failed';
          }
        }
        return (
          <span className={`status-badge ${statusClass}`}>
            <span className="status-text capitalize">
              {rowData?.status ? t(rowData.status) : t('na')}
            </span>
          </span>
        );
      },
    },
    {
      label: t('actions'),
      accessorKey: 'id' as keyof CommercialSessionInterface,
      sticky: 'right' as const,
      /**
       * Custom data for the actions column
       * @param {any} rowData session data
       * @returns {any} JSX element
       */
      customData: (rowData: CommercialSessionInterface) => {
        if (rowData?.status === 'inProgress') {
          const isAdminStart = (rowData as any).isChargingPointCheck === true;
          return (
            <div className={`stop-buttons ${isAdminStart ? 'disabled' : ''}`}>
              <span
                role="presentation"
                title={
                  isAdminStart
                    ? 'Session started by the admin, please go to the admin OCPP console to stop it.'
                    : t('stop')
                }
                className={isAdminStart ? 'admin-disabled-action' : ''}>
                <StopIcon onClick={isAdminStart ? undefined : () => handleStop(rowData)} />
              </span>
              <span
                role="presentation"
                title={
                  isAdminStart
                    ? 'Session started by the admin, please go to the admin OCPP console to stop it.'
                    : t('forceStop')
                }
                className={isAdminStart ? 'admin-disabled-action' : ''}>
                <SlashIcon onClick={isAdminStart ? undefined : () => handleForceStop(rowData)} />
              </span>
            </div>
          );
        }
        return <div>{t('na')}</div>;
      },
    },
  ];

  /**
   * Table pagination function
   * @param {any} value page and limit values
   */
  const pageNateLimitChange = (value: any) => {
    if (value?.page) {
      pageReference.current = value.page;
    }
    const queryParameters = GetParametersFromCommonFilters(filters);
    const sortValue = sortField && sortBy ? { sortField, sortBy } : {}; // no sort if empty
    fetchSessions({
      ...value,
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
      ...queryParameters,
      ...sortValue,
    });
  };

  /**
   * Handle the filter data clear function
   * @param {any} sortData - The sortData
   */
  const handleSortFunction = (sortData: any) => {
    pageReference.current = DefaultApiLimit.page;
    const queryParameters = GetParametersFromCommonFilters(filters);
    dispatch(setSessionSortData(sortData));
    fetchSessions({
      ...queryParameters,
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
      sortBy: sortData.sortBy,
      sortField: sortData.sortField,
      page: DefaultApiLimit.page,
      limit,
    });
  };

  /**
   * Poll session data every 10 seconds
   */
  // useEffect(() => {
  //   // let timeout: NodeJS.Timeout;
  //   // if (isPolling) {
  //   //   timeout = setTimeout(() => {
  //   //     handleRefresh();
  //   //     setIsPolling(false);
  //   //   }, 10000);
  //   // }
  //   // return () => clearTimeout(timeout);
  // }, [isPolling]);

  const isFirstMountFilters = React.useRef(true);

  useEffect(() => {
    if (isFirstMountFilters.current) {
      isFirstMountFilters.current = false;
      const queryParameters = GetParametersFromCommonFilters(filters);
      fetchSessions({
        ...queryParameters,
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
        page: DefaultApiLimit.page,
        limit,
      });
      return;
    }

    isStatusClicked.current = false;
    pageReference.current = DefaultApiLimit.page;
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchSessions({
      ...queryParameters,
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
      page: DefaultApiLimit.page,
      limit,
    });

    if (isFilterUpdated) {
      dispatch(disableSessionFiltersApplied());
    }
  }, [
    filters,
    activeStatus,
    dateRange.startDate,
    dateRange.endDate,
    chargingStationIds,
    chargingPoints,
  ]);

  useEffect(() => {
    if (
      searchParameters.get('stationId') ||
      searchParameters.get('deviceId') ||
      searchParameters.get('facilityId') ||
      searchParameters.get('userCustomId')
    ) {
      const parameters = new URLSearchParams(searchParameters);
      parameters.delete('stationId');
      parameters.delete('deviceId');
      parameters.delete('facilityId');
      parameters.delete('userCustomId');
      setSearchParameters(parameters, { replace: true });
    }
  }, []);

  useEffect(() => {
    if (
      !searchParameters.get('accountCustomId') &&
      !searchParameters.get('stationId') &&
      !searchParameters.get('deviceId')
    ) {
      if (searchParameters.get('action')) {
        const parameters = new URLSearchParams(searchParameters);
        parameters.delete('action');
        setSearchParameters(parameters, { replace: true });
      }
    }
  }, [searchParameters]);

  useEffect(() => {
    dispatch(
      setPageTitle({ title: t('sessions'), icon: <TransactionIcon />, count: totalResults }),
    );
  }, [totalResults, t]);

  /**
   * Check if status filter is currently active
   * @param {string} statusValue - The status value to check
   * @returns {boolean} True if active
   */
  const isStatusActive = (statusValue: string): boolean => {
    return activeStatus === statusValue;
  };

  /**
   * Handle status badge click to apply/toggle filter
   * @param {string} statusValue - The status value to filter by
   */
  const handleStatusClick = (statusValue: string) => {
    isStatusClicked.current = true;
    setActiveStatus((previous) => (previous === statusValue ? '' : statusValue));
  };

  return (
    <div className="payment-management-wrap" dir={i18n.dir()}>
      <ModalBox
        open={openStatus}
        title={modalView.title}
        content={modalView.content}
        closeModal={() => setOpenStatus(false)}
        header={modalView.header}
        rightCorner={modalView.rightCorner}
        notification={modalView.notification}
      />
      {!hideStatusBar && (
        <div className="stats-bar">
          <div className="stat-item" key="total">
            <span className="stat-label">{t('total')}</span>
            <span className="stat-value">{totalResults ?? 0}</span>
          </div>
          <div className="stat-divider" />
          <div
            className={`stat-badge finished-badge ${isStatusActive('finished') ? 'active' : ''}`}
            key="finished"
            role="presentation"
            onClick={() => handleStatusClick('finished')}>
            <span className="badge-dot" />
            {t('finished')}: {TotalFinising ?? 0}
          </div>
          <div
            className={`stat-badge inProgress-badge ${isStatusActive('inProgress') ? 'active' : ''}`}
            key="inProgress"
            role="presentation"
            onClick={() => handleStatusClick('inProgress')}>
            <span className="badge-dot" />
            {t('inProgress')}: {totalInprocess ?? 0}
          </div>
          <div
            className={`stat-badge terminated-badge ${isStatusActive('terminated') ? 'active' : ''}`}
            key="terminated"
            role="presentation"
            onClick={() => handleStatusClick('terminated')}>
            <span className="badge-dot" />
            {t('terminated')}: {totalterminated ?? 0}
          </div>
        </div>
      )}
      <div className="table-content">
        <CustomTable
          dataKey="CommercialSessions"
          version="v2"
          header={column}
          data={sessions}
          limit={limit}
          page={pageReference.current || page || 1}
          totalPages={totalPages}
          totalResults={totalResults}
          loading={loading}
          serialNo
          sortData={{ sortBy, sortField }}
          handleSortChange={handleSortFunction}
          pageLimitChange={pageNateLimitChange}
          exportDataPdfOrCSV={permissionCheck('cpoSessionExport') ? exportDataPdfOrCSV : undefined}
          exportLoader={exportLoader}
          sizeChangeButtonRequired
          filters={filters}
          filtersToDispatch={updateSessionFilters}
          filterListLoading={filterListLoading}
          filterListToDispatch={fetchListSessionFilters}
          rightTopContent={
            <>
              {portalType === 'lllll' && (
                <div className="revenue-management-toggle-wrap">
                  <CustomRadioButton
                    name=""
                    value="commercial"
                    label={t('commercialCharger')}
                    selected={currentPage === 'commercial'}
                    onChangeFunc={handleAvailabilityChange}
                  />
                  <CustomRadioButton
                    name=""
                    value="personal"
                    label={t('personalCharger')}
                    selected={currentPage === 'personal'}
                    onChangeFunc={handleAvailabilityChange}
                  />
                </div>
              )}
              {renderDateFilters()}
            </>
          }
          onHiddenColumnsChange={setHiddenColumns}
          requiredRefreshToggle
          refreshFunction={handleAutoRefresh}
          refreshRequired
          handleRefresh={handleRefresh}
          refershCurrentPages={handleRefresh}
          hoverLabel={t('refresh')}
          checkbox
          autoRefreshEnabled
          isAutoRefreshActive={isAutoRefreshActive}
          onAutoRefreshChange={handleAutoRefreshToggle}
          selectedRows={selectedRows}
          onSelectedRowsChange={setSelectedRows}
          selectKey="id"
          isRowSelectable={(row) =>
            row.status === 'inProgress' && !(row as any).isChargingPointCheck
          }
          sidePanelTopLeftContent={renderSidePanelTopLeftContent() ?? undefined}
          onSidePanelClose={handleSidePanelClose}
          rowClickParamsKey="sessionId"
          rowClickEvent={handleOpenSidePanelOnRowClick}
          rowClickSidePanel={
            <CommercialSessionSidePanel
              fetchIdFrom="queryParams"
              selectedSession={selectedSessionRow}
              sessions={sessions}
            />
          }
          onClearFilterChange={() => {
            clearDateRange();
          }}
          created
          lastUpdated
          height={330}
        />
        <CustomActionBar
          selectedRows={selectedRows}
          onSelectedRowsChange={setSelectedRows}
          actions={[
            {
              label: t('stop'),
              // icon: <StopIcon />,
              buttonType: 'danger-outline',
              /**
               * handle stop
               * @todo
               */
              onClick: () => handleStop(selectedRows),
            },
            {
              label: t('forceStop'),
              // icon: <SlashIcon />,
              buttonType: 'error',
              /**
               * handle force stop
               * @todo
               */
              onClick: () => handleForceStop(selectedRows),
            },
          ]}
        />
      </div>
    </div>
  );
}

CommercialSessionTable.defaultProps = {
  chargingStationIds: undefined,
  chargingPoints: undefined,
  defaultStatus: '',
  hideStatusBar: false,
};

export default CommercialSessionTable;
